import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { authorApi } from '../services/api';
import { AuthorWithArticles } from '../types';
import { ArticleCard } from '../components/ArticleCard';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const AutorPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [author, setAuthor] = useState<AuthorWithArticles | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    authorApi
      .getAuthorBySlug(slug)
      .then(setAuthor)
      .catch(() => setAuthor(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Carregando perfil...</div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Autor não encontrado</h1>
          <Link to="/" className="text-blue-600 hover:underline">Voltar para o início</Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `${SITE_URL}/autores/${author.slug}`;
  const joined = format(new Date(author.joinedAt), "MMMM 'de' yyyy", { locale: ptBR });
  const description =
    author.bio ||
    `Perfil de ${author.name} no ${SITE_NAME}: artigos publicados e áreas de interesse.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': author.isTeam ? 'Organization' : 'Person',
    name: author.name,
    url: canonicalUrl,
    ...(author.photoUrl ? { image: author.photoUrl } : {}),
    ...(author.bio ? { description: author.bio } : {}),
    ...(author.role && !author.isTeam ? { jobTitle: author.role } : {}),
    worksFor: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>{author.name} | Autores | {SITE_NAME}</title>
        <meta name="description" content={description.slice(0, 160)} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${author.name} | ${SITE_NAME}`} />
        <meta property="og:description" content={description.slice(0, 160)} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <Link to="/autores" className="hover:text-blue-600 transition">Autores</Link>
            <span>›</span>
            <span className="text-gray-700">{author.name}</span>
          </nav>

          {/* Profile card */}
          <div className="bg-white rounded-xl shadow p-8 mb-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="flex-shrink-0 w-24 h-24 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-4xl font-bold overflow-hidden">
                {author.photoUrl ? (
                  <img src={author.photoUrl} alt={author.name} className="w-full h-full object-cover" />
                ) : (
                  author.name.charAt(0)
                )}
              </div>
              <div className="flex-grow">
                <h1 className="text-3xl font-bold text-gray-900 mb-1">{author.name}</h1>
                {author.role && <p className="text-blue-600 font-semibold text-sm mb-3">{author.role}</p>}
                {author.bio && <p className="text-gray-700 leading-relaxed mb-4">{author.bio}</p>}

                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500 mb-3">
                  <span>No {SITE_NAME} desde {joined}</span>
                  {author._count && <span>{author._count.articles} artigos publicados</span>}
                </div>

                {author.interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {author.interests.map((interest) => (
                      <span key={interest} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                        {interest}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-3 text-sm">
                  {author.twitter && (
                    <a href={author.twitter} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      X / Twitter
                    </a>
                  )}
                  {author.linkedin && (
                    <a href={author.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      LinkedIn
                    </a>
                  )}
                  {author.website && (
                    <a href={author.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      Site
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Articles */}
          {author.recentArticles.length > 0 && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Artigos recentes</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {author.recentArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
